import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Download, Send, User, ChevronRight, CheckCircle, Clock, 
  TrendingUp, BarChart2, RefreshCw, MessageCircle, 
  Brain, Coffee, Sparkles, ShieldCheck, Play 
} from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

// --- CONFIGURAÇÕES ---
const EMAIL_RH = "roempregamais@gmail.com"; 
const WHATSAPP_NUMBER = "5569999914037"; 

// --- PERFIS DETALHADOS ---
const PROFILES_INFO = {
  D: {
    title: "Executor (Dominância)",
    fullTitle: "Perfil Executor (Dominância)",
    color: "text-red-600",
    bg: "bg-red-100",
    border: "border-red-500",
    description: "Orientado para ação e resultados. Direto, assertivo e movido a desafios. Assume liderança naturalmente.",
    characteristics: ["Autoconfiante e determinado", "Gosta de liberdade", "Focado em metas", "Competitivo"],
    strengths: ["Tomada de decisão rápida", "Liderança sob pressão", "Visão de resultado"],
    pointsToImprove: ["Impaciência com detalhes", "Escuta ativa", "Pode parecer autoritário"]
  },
  I: {
    title: "Comunicador (Influência)",
    fullTitle: "Perfil Comunicador (Influência)",
    color: "text-yellow-600",
    bg: "bg-yellow-100",
    border: "border-yellow-500",
    description: "Sociável, entusiasta e persuasivo. Valoriza relacionamentos e ambientes dinâmicos. Motiva a equipe com otimismo.",
    characteristics: ["Carismático e otimista", "Trabalho em equipe", "Comunicativo", "Criativo"],
    strengths: ["Comunicação", "Motivação de pessoas", "Adaptação a mudanças"],
    pointsToImprove: ["Organização e prazos", "Prometer demais", "Atenção a detalhes técnicos"]
  },
  S: {
    title: "Planejador (Estabilidade)",
    fullTitle: "Perfil Planejador (Estabilidade)",
    color: "text-green-600",
    bg: "bg-green-100",
    border: "border-green-500",
    description: "Paciente, leal e consistente. Valoriza segurança e harmonia. É o ponto de equilíbrio da equipe.",
    characteristics: ["Calmo e equilibrado", "Gosta de rotinas", "Bom ouvinte", "Persistente"],
    strengths: ["Cooperação e suporte", "Trabalho consistente", "Mediação de conflitos"],
    pointsToImprove: ["Dificuldade em dizer 'não'", "Resistência a mudanças", "Decisão sob pressão"]
  },
  C: {
    title: "Analista (Conformidade)",
    fullTitle: "Perfil Analista (Conformidade)",
    color: "text-blue-600",
    bg: "bg-blue-100",
    border: "border-blue-500",
    description: "Preciso, lógico e detalhista. Valoriza qualidade e exatidão. Segue regras para garantir a perfeição.",
    characteristics: ["Detalhist e organizado", "Analítico", "Focado em qualidade", "Observador"],
    strengths: ["Análise crítica", "Identificação de riscos", "Alta qualidade técnica"],
    pointsToImprove: ["Autocrítica excessiva", "Centralização de tarefas", "Paralisia por análise"]
  }
};

// --- BANCO DE PERGUNTAS ---
const rawQuestions = [
  { id: 1, text: "A empresa decide mudar repentinamente o software de gestão. A implementação começa amanhã. Qual é sua reação imediata?", options: [{ text: "Fico impaciente com a transição, mas foco em aprender o essencial para não perder produtividade.", type: "D" }, { text: "Vejo como novidade, converso com os colegas e tento animar o clima durante a mudança.", type: "I" }, { text: "Sinto desconforto. Preciso de tempo para adaptar e gostaria de um treinamento passo a passo.", type: "S" }, { text: "Preocupo-me com a migração dos dados. Quero ler o manual para garantir que nada será perdido.", type: "C" }] },
  { id: 2, text: "Você lidera uma reunião e dois colegas discutem calorosamente. O tempo está acabando. O que você faz?", options: [{ text: "Interrompo a discussão, tomo a decisão final eu mesmo e encerro a reunião.", type: "D" }, { text: "Uso humor para quebrar a tensão e peço que votemos na ideia mais empolgante.", type: "I" }, { text: "Tento acalmar os ânimos e sugiro adiarmos a decisão para não gerar inimizades.", type: "S" }, { text: "Peço que parem de opinar e apresentem dados concretos. A decisão deve ser baseada em fatos.", type: "C" }] },
  { id: 3, text: "Seu gestor lhe entrega uma tarefa complexa com instruções vagas e prazo curto. Como procede?", options: [{ text: "Assumo o risco. Defino o melhor caminho e entrego, confiando na minha capacidade.", type: "D" }, { text: "Uso minha rede de contatos para ver se alguém já fez isso e improviso uma solução criativa.", type: "I" }, { text: "Sinto pressão. Busco o gestor para alinhar expectativas e pedir mais clareza.", type: "S" }, { text: "Paro tudo. Faço uma lista de perguntas detalhadas, pois não posso começar sem entender os critérios.", type: "C" }] },
  { id: 4, text: "Você nota uma falha num processo antigo que gera pequenos prejuízos. Ninguém mais viu.", options: [{ text: "Vou à diretoria, exponho o problema e apresento minha solução imediatamente.", type: "D" }, { text: "Comento com colegas para validar minha descoberta e mobilizar um grupo para resolver.", type: "I" }, { text: "Fico receoso de expor o erro de alguém. Corrijo discretamente ou aguardo o momento certo.", type: "S" }, { text: "Calculo o prejuízo exato, crio um relatório com evidências e envio formalmente.", type: "C" }] },
  { id: 5, text: "Ao receber feedback negativo sobre um projeto onde se dedicou muito, seu primeiro pensamento é:", options: [{ text: "Eles não entenderam o resultado final. Vou provar que estou certo na próxima.", type: "D" }, { text: "Será que isso vai afetar meu relacionamento com a equipe? Espero que ainda gostem de mim.", type: "I" }, { text: "Eu sabia que devia ter seguido o padrão anterior. Não devia ter arriscado.", type: "S" }, { text: "Onde exatamente eu errei? Preciso ver os critérios técnicos dessa avaliação.", type: "C" }] },
  { id: 6, text: "Você precisa convencer um cliente difícil a fechar contrato hoje. Qual é sua 'arma' principal?", options: [{ text: "Assertividade. Mostro que é pegar ou largar e foco na vantagem competitiva.", type: "D" }, { text: "Carisma. Crio conexão pessoal, conto histórias e uso entusiasmo.", type: "I" }, { text: "Confiança. Mostro que estaremos ao lado dele a longo prazo, oferecendo segurança.", type: "S" }, { text: "Lógica. Apresento gráficos e dados que provam que somos a melhor opção racional.", type: "C" }] },
  { id: 7, text: "O ambiente de trabalho ideal para você atingir seu potencial máximo deve ser:", options: [{ text: "Dinâmico, competitivo e com liberdade para decisões autônomas.", type: "D" }, { text: "Colaborativo, divertido e aberto a muitas interações sociais.", type: "I" }, { text: "Harmonioso, estável e previsível, com apoio mútuo.", type: "S" }, { text: "Organizado, silencioso e estruturado, onde a qualidade é prioridade.", type: "C" }] },
  { id: 8, text: "Como você lida com regras muito rígidas e burocracia excessiva?", options: [{ text: "Sinto-me preso. Se atrapalha o resultado, tendo a questionar ou buscar atalhos.", type: "D" }, { text: "Tento usar meu jeito para flexibilizar as regras, pois burocracia mata a criatividade.", type: "I" }, { text: "Sinto-me seguro. Regras evitam conflitos e mantêm a ordem.", type: "S" }, { text: "Sinto-me confortável. A burocracia garante padronização e evita erros.", type: "C" }] },
  { id: 9, text: "Em um projeto de equipe, qual papel você naturalmente assume?", options: [{ text: "O Líder. Defino metas, cobro prazos e mantenho o foco no objetivo.", type: "D" }, { text: "O Motivador. Mantenho o astral alto e facilito a comunicação.", type: "I" }, { text: "O Apoiador. Faço o trabalho duro, ajudo quem precisa e garanto a coesão.", type: "S" }, { text: "O Especialista. Verifico a qualidade e corrijo detalhes técnicos.", type: "C" }] },
  { id: 10, text: "O que faria você pedir demissão de um emprego hoje?", options: [{ text: "Falta de desafios, estagnação ou perda de autonomia.", type: "D" }, { text: "Ambiente frio, isolamento social ou falta de reconhecimento público.", type: "I" }, { text: "Mudanças constantes, instabilidade ou pressão agressiva.", type: "S" }, { text: "Desorganização, falta de padrões ou ter que entregar trabalho malfeito.", type: "C" }] },
  { id: 11, text: "Seu colega está atrasando a parte dele no projeto e isso vai te prejudicar. Você:", options: [{ text: "Cobro ele diretamente e exijo que termine logo ou passarei a tarefa para outro.", type: "D" }, { text: "Converso com ele de forma amigável para entender se está tudo bem e o encorajo.", type: "I" }, { text: "Ofereço ajuda para terminarmos juntos, evitando conflito ou atraso.", type: "S" }, { text: "Reviso o cronograma e mostro a ele exatamente como o atraso impacta o fluxo lógico.", type: "C" }] },
  { id: 12, text: "Ao planejar suas férias, como você age?", options: [{ text: "Decido o destino de última hora e vou resolver os problemas quando chegar lá. Gosto de aventura.", type: "D" }, { text: "Escolho o lugar mais badalado onde meus amigos estão indo. Quero diversão.", type: "I" }, { text: "Planejo com meses de antecedência, reservo tudo e evito surpresas desagradáveis.", type: "S" }, { text: "Pesquiso cada detalhe, comparo preços em planilhas e crio um roteiro dia a dia.", type: "C" }] },
  { id: 13, text: "Durante uma apresentação importante, o projetor queima. O que você faz?", options: [{ text: "Continuo falando sem slides, com voz firme, mantendo o controle da sala.", type: "D" }, { text: "Faço uma piada sobre a tecnologia e transformo a apresentação num bate-papo interativo.", type: "I" }, { text: "Peço desculpas calmamente e aguardo alguém da técnica resolver, para não improvisar.", type: "S" }, { text: "Distribuo o material impresso que trouxe de reserva, pois eu já previa essa possibilidade.", type: "C" }] },
  { id: 14, text: "Qual é a sua maior fraqueza no ambiente corporativo?", options: [{ text: "Posso ser impaciente e atropelar os outros quando quero resultados rápidos.", type: "D" }, { text: "Posso perder o foco com conversas e ter dificuldade com detalhes minuciosos.", type: "I" }, { text: "Tenho dificuldade em dizer 'não' e acabo me sobrecarregando para agradar.", type: "S" }, { text: "Posso ser perfeccionista demais e demorar para entregar por revisar excessivamente.", type: "C" }] },
  { id: 15, text: "Se você ganhasse na loteria hoje, o que faria com sua carreira?", options: [{ text: "Abriria meu próprio império empresarial para comandar tudo do meu jeito.", type: "D" }, { text: "Viajaria o mundo fazendo networking e festas, talvez investindo em entretenimento.", type: "I" }, { text: "Manteria meu padrão de vida discreto e investiria em segurança para minha família.", type: "S" }, { text: "Estudaria investimentos a fundo para gerenciar o capital com risco zero.", type: "C" }] },
  { id: 16, text: "Você precisa demitir um funcionário que é gente boa, mas não entrega resultados. Como faz?", options: [{ text: "Sou direto e rápido. Explico que os números não foram atingidos e encerro o contrato.", type: "D" }, { text: "Sofro com a decisão. Tento falar com muito tato para que ele não saia magoado comigo.", type: "I" }, { text: "Adio a decisão o máximo possível, esperando que ele melhore sozinho.", type: "S" }, { text: "Apresento o histórico de avaliações dele, mostrando graficamente onde ele falhou.", type: "C" }] },
  { id: 17, text: "O que te deixa mais entediado?", options: [{ text: "Tarefas repetitivas que não trazem desafio ou crescimento visível.", type: "D" }, { text: "Trabalhar sozinho em uma sala fechada sem ninguém para conversar.", type: "I" }, { text: "Mudanças bruscas de rotina que me obrigam a replanejar tudo a toda hora.", type: "S" }, { text: "Reuniões sem pauta definida onde as pessoas falam muito e decidem pouco.", type: "C" }] },
  { id: 18, text: "Como você prefere receber reconhecimento?", options: [{ text: "Com uma promoção, um bônus financeiro ou mais poder de decisão.", type: "D" }, { text: "Com um elogio público, na frente de toda a equipe ou da empresa.", type: "I" }, { text: "Com um agradecimento sincero e privado, reconhecendo meu esforço.", type: "S" }, { text: "Com um feedback escrito detalhando a qualidade técnica do meu trabalho.", type: "C" }] },
  { id: 19, text: "Diante de um conflito de interesses éticos na empresa, você:", options: [{ text: "Confronto a liderança imediatamente para resolver a situação ou saio.", type: "D" }, { text: "Tento conversar com as pessoas envolvidas para entender os motivos e apaziguar.", type: "I" }, { text: "Fico desconfortável e tento não me envolver, esperando que se resolva.", type: "S" }, { text: "Consulto o código de conduta e as leis vigentes antes de tomar qualquer atitude.", type: "C" }] },
  { id: 20, text: "Qual frase define melhor sua filosofia de vida?", options: [{ text: "O mundo pertence a quem ousa fazer.", type: "D" }, { text: "A vida é melhor quando compartilhada com amigos.", type: "I" }, { text: "Devagar e sempre se vai ao longe.", type: "S" }, { text: "A perfeição está nos detalhes.", type: "C" }] },
  { id: 21, text: "Você recebe um manual de instruções de 100 páginas para um novo equipamento. Você:", options: [{ text: "Ignoro o manual, ligo o aparelho e aprendo mexendo.", type: "D" }, { text: "Pergunto a alguém que já sabe usar para me explicar o básico.", type: "I" }, { text: "Guardo o manual em local seguro para consultar se tiver problemas.", type: "S" }, { text: "Leio o manual do início ao fim antes de ligar o aparelho na tomada.", type: "C" }] },
  { id: 22, text: "Em um jantar de negócios, seu comportamento é:", options: [{ text: "Falo de negócios o tempo todo, buscando fechar parcerias.", type: "D" }, { text: "Sou o centro das atenções, conto piadas e entretenho a mesa.", type: "I" }, { text: "Ouço mais do que falo, garantindo que todos estejam confortáveis.", type: "S" }, { text: "Observo a etiqueta, escolho os pratos com cuidado e falo pontualmente.", type: "C" }] },
  { id: 23, text: "Quando você precisa aprender algo novo, prefere:", options: [{ text: "Aprender na prática, errando e acertando rápido (Trial & Error).", type: "D" }, { text: "Aprender em grupo, discutindo o tema em workshops dinâmicos.", type: "I" }, { text: "Ter um mentor que me guie passo a passo com paciência.", type: "S" }, { text: "Ter acesso a livros, apostilas e cursos estruturados.", type: "C" }] },
  { id: 24, text: "Seu escritório está uma bagunça. O que você faz?", options: [{ text: "Mando alguém arrumar para mim ou arrumo só o essencial para trabalhar.", type: "D" }, { text: "A bagunça não me incomoda, desde que eu encontre o que preciso.", type: "I" }, { text: "Tiro um dia para arrumar tudo e me sentir em paz novamente.", type: "S" }, { text: "Não deixo bagunçar. Tenho um sistema de arquivamento impecável.", type: "C" }] },
  { id: 25, text: "Você precisa dar uma má notícia a um cliente. Como aborda?", options: [{ text: "Vou direto ao ponto, sem rodeios, e já proponho a solução.", type: "D" }, { text: "Tento amaciar a notícia com uma conversa agradável antes e depois.", type: "I" }, { text: "Peço desculpas sinceras e mostro empatia pelo problema dele.", type: "S" }, { text: "Explico tecnicamente porque o problema ocorreu para me isentar de culpa pessoal.", type: "C" }] },
  { id: 26, text: "O que você pensa sobre assumir riscos?", options: [{ text: "Adoro. Sem risco não há ganho elevado.", type: "D" }, { text: "Gosto, se for algo divertido e que traga visibilidade.", type: "I" }, { text: "Evito. Prefiro a segurança do que já conheço.", type: "S" }, { text: "Calculo. Só assumo riscos se a probabilidade de erro for mínima.", type: "C" }] },
  { id: 27, text: "Você está em um grupo e ninguém toma a iniciativa. Você:", options: [{ text: "Imediatamente assumo o comando e digo o que fazer.", type: "D" }, { text: "Começo a puxar assunto para ver o que o grupo quer fazer.", type: "I" }, { text: "Aguardo pacientemente até que alguém se manifeste.", type: "S" }, { text: "Começo a organizar as opções disponíveis mentalmente.", type: "C" }] },
  { id: 28, text: "Como você reage a uma pessoa muito lenta?", options: [{ text: "Fico irritado e acabo fazendo a tarefa por ela para andar logo.", type: "D" }, { text: "Tento animá-la para ver se ela acelera o ritmo.", type: "I" }, { text: "Tenho paciência e ofereço ajuda se ela precisar.", type: "S" }, { text: "Entendo que cada um tem seu tempo, desde que faça bem feito.", type: "C" }] },
  { id: 29, text: "O que mais te atrai em um novo projeto?", options: [{ text: "O potencial de lucro e poder que ele trará.", type: "D" }, { text: "As pessoas novas que vou conhecer.", type: "I" }, { text: "A garantia de que terei emprego por longo prazo.", type: "S" }, { text: "A oportunidade de aprender técnicas novas e complexas.", type: "C" }] },
  { id: 30, text: "Para você, o sucesso é:", options: [{ text: "Ser o melhor, o primeiro e o mais respeitado.", type: "D" }, { text: "Ser popular, amado e ter muitos amigos.", type: "I" }, { text: "Ter paz de espírito, família segura e estabilidade.", type: "S" }, { text: "Fazer um trabalho perfeito, sem falhas e ser referência técnica.", type: "C" }] }
];

const DiscTestPage = () => {
  const [step, setStep] = useState(1);
  const [userForm, setUserForm] = useState({ name: '', age: '' });
  const [questions, setQuestions] = useState([]); 
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSending, setIsSending] = useState(false); // Estado de envio
  const resultRef = useRef(null);

  // --- LÓGICA DE IDADE ---
  const getAgeInsight = (age) => {
    const numAge = parseInt(age);
    if (!numAge) return null;

    if (numAge < 25) {
      return { stage: "Início de Jornada & Descoberta", description: "Você está na fase de exploração e construção de identidade profissional. Seu perfil DISC funciona como sua bússola inicial.", advice: "Use suas características dominantes para se destacar, mas mantenha a mente aberta para aprender (Feedback). É a hora ideal para arriscar e entender onde você brilha." };
    } else if (numAge < 40) {
      return { stage: "Consolidação & Crescimento", description: "Fase de focar em resultados e especialização. Você já sabe o que faz bem; agora é hora de lapidar seu perfil para liderança.", advice: "Foque em como seu comportamento gera valor tangível. Se for líder, cuidado para não impor seu estilo aos outros; use seu autoconhecimento para gerir a equipe." };
    } else if (numAge < 55) {
      return { stage: "Liderança & Estratégia", description: "Sua experiência técnica se une ao comportamento. Espera-se de você visão estratégica e inteligência emocional.", advice: "Use seu perfil para mentorar os mais jovens. Seu desafio agora não é apenas 'fazer', mas 'influenciar' e garantir a estabilidade ou inovação da organização." };
    } else {
      return { stage: "Legado & Sabedoria", description: "Você possui uma visão holística que poucos têm. Seu perfil comportamental é uma ferramenta para aconselhamento e direção.", advice: "Seu maior ativo é o equilíbrio. Use seus pontos fortes para deixar um legado cultural positivo e preparar sucessores, evitando conflitos desnecessários." };
    }
  };

  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // PASSO 1: Captura os dados e vai para as instruções
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!userForm.name || !userForm.age) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }
    setStep(1.5); // Vai para as instruções
  };

  // PASSO 1.5: Começa realmente o teste
  const handleStartTest = () => {
    const shuffled = shuffleArray(rawQuestions);
    setQuestions(shuffled);
    setStep(2);
  };

  const handleReset = () => {
    setStep(1);
    setAnswers({});
    setCurrentQuestion(0);
    setUserForm({ name: '', age: '' });
    window.scrollTo(0, 0);
  };

  const handleAnswer = (type) => {
    setIsTransitioning(true);
    setTimeout(() => {
        setAnswers(prev => ({ ...prev, [questions[currentQuestion].id]: type }));
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(prev => prev + 1);
            setIsTransitioning(false);
        } else {
            setStep(3);
            setIsTransitioning(false);
            window.scrollTo(0, 0);
        }
    }, 200); 
  };

  const calculateResult = () => {
    const counts = { D: 0, I: 0, S: 0, C: 0 };
    Object.values(answers).forEach(type => counts[type]++);
    const total = rawQuestions.length;
    const percentages = {
      D: Math.round((counts.D / total) * 100),
      I: Math.round((counts.I / total) * 100),
      S: Math.round((counts.S / total) * 100),
      C: Math.round((counts.C / total) * 100),
    };
    const dominantTrait = Object.keys(percentages).reduce((a, b) => percentages[a] > percentages[b] ? a : b);
    return { ...percentages, dominantTrait };
  };

  const result = step === 3 ? calculateResult() : null;
  const ageInsight = step === 3 ? getAgeInsight(userForm.age) : null;

  // --- FUNÇÃO PARA GERAR PDF (BAIXAR) ---
const generatePDF = async () => {
  const element = resultRef.current;
  if (!element) return;

  const toastId = toast.loading("Gerando PDF...");

  try {
    // 1️⃣ Clona o resultado
    const clone = element.cloneNode(true);

    // 2️⃣ Container invisível
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-99999px";
    container.style.top = "0";
    container.style.width = element.offsetWidth + "px";
    container.style.background = "#ffffff";
    container.appendChild(clone);

    // 3️⃣ Injeta o CSS do PDF
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/print.css";
    container.appendChild(link);

    document.body.appendChild(container);

    // 4️⃣ Espera o CSS carregar
    await new Promise(r => setTimeout(r, 300));

    // 5️⃣ Gera o PDF
    await html2pdf().set({
      margin: 10,
      filename: "resultado_DISC.pdf",
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 3,
        backgroundColor: "#ffffff",
        useCORS: true,
        scrollY: 0
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
    }).from(clone).save();

    document.body.removeChild(container);
    toast.success("PDF gerado!", { id: toastId });

  } catch (err) {
    console.error(err);
    toast.error("Erro ao gerar PDF", { id: toastId });
  }
};


  // --- FUNÇÃO DE ENVIO AUTOMÁTICO (VISUAL PREMIUM) ---
  const handleEmailDirect = async () => {
    setIsSending(true);
    toast.info("Gerando relatório visual...", { duration: 2000 });

    const element = resultRef.current;
    
    // 1. Gera o PDF em alta definição
    const pdfBlob = await html2pdf().set({
        margin: [10, 10, 10, 10],
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    }).from(element).outputPdf('blob');

    // 2. Função "Barra Gráfica Premium"
    const createPremiumBar = (percentage) => {
        const totalBlocks = 15; 
        const filledCount = Math.round((percentage / 100) * totalBlocks);
        const emptyCount = totalBlocks - filledCount;
        const filled = '▰'; 
        const empty = '▱';
        return `${filled.repeat(filledCount)}${empty.repeat(emptyCount)}`;
    };

    const formData = new FormData();
    formData.append('_captcha', 'false'); 
    formData.append('_template', 'table'); 
    formData.append('_subject', `📑 Resultado DISC: ${userForm.name} (${PROFILES_INFO[result.dominantTrait].title})`);
    
    formData.append('👤 Candidato', userForm.name.toUpperCase());
    formData.append('🎂 Idade', `${userForm.age} anos`);
    formData.append('🏆 Perfil Principal', `★ ${PROFILES_INFO[result.dominantTrait].fullTitle} ★`);
    formData.append(' ', ' '); 

    formData.append('🔴 Executor (D)', `${createPremiumBar(result.D)}  ${result.D}%`);
    formData.append('🟡 Comunicador (I)', `${createPremiumBar(result.I)}  ${result.I}%`);
    formData.append('🟢 Planejador (S)', `${createPremiumBar(result.S)}  ${result.S}%`);
    formData.append('🔵 Analista (C)', `${createPremiumBar(result.C)}  ${result.C}%`);
    
    formData.append('  ', '  '); 
    
    const wppLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá ${userForm.name}, vimos seu resultado DISC.`;
    formData.append('📱 Contato Rápido', wppLink);
    formData.append('📄 Anexo', 'O PDF completo com gráficos detalhados está anexado abaixo.');

    const pdfFile = new File([pdfBlob], `DISC_${userForm.name.replace(/\s+/g, '_')}.pdf`, { type: 'application/pdf' });
    formData.append('attachment', pdfFile);

    try {
        await fetch(`https://formsubmit.co/ajax/${EMAIL_RH}`, {
            method: 'POST',
            body: formData
        });
        toast.success("Relatório enviado com sucesso!");
    } catch (error) {
        console.error(error);
        toast.error("Erro no envio. Verifique sua conexão.");
    } finally {
        setIsSending(false);
    }
  };

  const handleWhatsApp = () => {
    const message = `Olá, meu resultado DISC foi: ${PROFILES_INFO[result.dominantTrait].title}.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // --- NOVO GRÁFICO DE BARRAS ---
const BarChart = ({ data }) => {
  return (
    /* Adicionamos bg-white e p-4 para garantir que o fundo do gráfico seja sempre branco no PDF */
    <div className="w-full h-60 md:h-80 flex items-end justify-between gap-3 px-2 font-heading bg-white p-4 rounded-xl">
      {Object.entries(data).map(([key, value]) => {
        if (key === 'dominantTrait') return null;
        
        // Cores sólidas e fortes (HEX) que o html2canvas entende perfeitamente
        const styleMap = {
          D: { color: '#dc2626' }, // Vermelho
          I: { color: '#ca8a04' }, // Amarelo/Dourado
          S: { color: '#16a34a' }, // Verde
          C: { color: '#2563eb' }  // Azul
        }[key];

        return (
          <div key={key} className="flex flex-col items-center justify-end w-1/4 h-full group relative">
            {/* Texto do percentual: Forçamos a cor #1e293b (quase preto) via inline style */}
            <span 
              className="mb-2 font-bold text-xs md:text-base" 
              style={{ color: '#1e293b', opacity: 1 }}
            >
              {value}%
            </span>

            <div 
              className="w-full rounded-t-lg relative"
              style={{ 
                height: `${value}%`, 
                minHeight: '8px',
                backgroundColor: styleMap.color, // Cor da barra fixa
                opacity: 1, // Garante que não esteja transparente
                WebkitPrintColorAdjust: 'exact'
              }}
            >
              {/* Linha de brilho superior (opcional, removida no print para maior clareza) */}
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-white/30 print:hidden" />
            </div>

            {/* Letra do Perfil (D, I, S, C): Cor fixa baseada no perfil */}
            <span 
              className="mt-2 text-sm md:text-xl font-bold" 
              style={{ color: styleMap.color, opacity: 1 }}
            >
              {key}
            </span>
          </div>
        );
      })}
    </div>
  );
};

  const currentQ = (step === 2 && questions.length > 0 && currentQuestion < questions.length) ? questions[currentQuestion] : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-6 px-4 md:py-12 print:bg-white print:p-0 transition-colors duration-300">
      <div className="max-w-4xl mx-auto bg-white dark:bg-slate-900 rounded-2xl md:rounded-3xl shadow-lg md:shadow-xl overflow-hidden border border-slate-100 dark:border-slate-800 print:shadow-none print:border-none">
        
        <div className="bg-petroleum-500 dark:bg-slate-800 p-6 md:p-8 text-center print:hidden border-b dark:border-slate-700 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500" />
          <h1 className="text-2xl md:text-3xl font-bold text-white font-heading mb-1">Perfil Comportamental</h1>
          <p className="text-teal-100 dark:text-teal-400 text-xs md:text-sm opacity-90">Metodologia DISC</p>
        </div>

        {/* PASSO 1: FORMULÁRIO INICIAL */}
        {step === 1 && (
          <div className="p-6 md:p-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-8 md:mb-10">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-sm">
                <User size={32} className="md:w-10 md:h-10" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">Bem-vindo(a)!</h2>
              <p className="text-sm md:text-base text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
                Preencha seus dados para iniciar a avaliação gratuita.
              </p>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-6 max-w-md mx-auto">
              <div className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-600 dark:text-slate-300">Nome Completo</Label>
                    <Input id="name" value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} placeholder="Seu nome" className="h-12 rounded-xl" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="age" className="text-slate-600 dark:text-slate-300">Idade</Label>
                    <Input id="age" type="number" value={userForm.age} onChange={e => setUserForm({...userForm, age: e.target.value})} placeholder="Sua idade" className="h-12 rounded-xl" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-12 md:h-14 text-base md:text-lg shadow-lg shadow-teal-600/20 transition-all hover:scale-[1.02]">
                Continuar <ChevronRight className="ml-2" />
              </Button>
            </form>
          </div>
        )}

        {/* PASSO 1.5: INSTRUÇÕES (NOVO) */}
        {step === 1.5 && (
            <div className="p-6 md:p-12 animate-in fade-in zoom-in-95 duration-500">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <Brain size={32} />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">Antes de começarmos...</h2>
                    <p className="text-slate-500 dark:text-slate-400 mt-2">Leia com atenção para garantir um resultado preciso.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto mb-8">
                    <div className="bg-slate-50 dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 flex items-start gap-4">
                        <div className="p-3 bg-purple-100 text-purple-600 rounded-xl"><Coffee size={24}/></div>
                        <div>
                            <h3 className="font-bold text-slate-800 dark:text-white">Ambiente Tranquilo</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Faça o teste em um lugar calmo, sem distrações. Reserve cerca de 10 minutos.</p>
                        </div>
                    </div>
                    
                    <div className="bg-slate-50 dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 flex items-start gap-4">
                        <div className="p-3 bg-teal-100 text-teal-600 rounded-xl"><Sparkles size={24}/></div>
                        <div>
                            <h3 className="font-bold text-slate-800 dark:text-white">Seja Espontâneo</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Não pense muito. A primeira resposta que vier à mente geralmente é a mais correta.</p>
                        </div>
                    </div>

                    <div className="bg-slate-50 dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 flex items-start gap-4">
                        <div className="p-3 bg-orange-100 text-orange-600 rounded-xl"><ShieldCheck size={24}/></div>
                        <div>
                            <h3 className="font-bold text-slate-800 dark:text-white">Seja Sincero</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Responda como você <strong>realmente é</strong>, e não como gostaria de ser ou como acham que você deveria ser.</p>
                        </div>
                    </div>

                    <div className="bg-slate-50 dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 flex items-start gap-4">
                        <div className="p-3 bg-pink-100 text-pink-600 rounded-xl"><TrendingUp size={24}/></div>
                        <div>
                            <h3 className="font-bold text-slate-800 dark:text-white">Não há Certo ou Errado</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Não existem perfis melhores ou piores. Cada um tem suas forças únicas.</p>
                        </div>
                    </div>
                </div>

                <div className="max-w-md mx-auto">
                    <Button onClick={handleStartTest} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-14 text-lg shadow-lg shadow-teal-600/20 transition-all hover:scale-[1.02] flex items-center justify-center gap-2">
                        <Play size={20} fill="currentColor" /> Entendi, Começar Agora
                    </Button>
                </div>
            </div>
        )}

        {/* PASSO 2: PERGUNTAS */}
        {step === 2 && currentQ && (
          <div className="p-4 md:p-12 min-h-[500px] md:min-h-[600px] flex flex-col">
            <div className="mb-6 md:mb-8">
                <div className="flex justify-between items-center text-xs md:text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">
                    <span>Progresso</span>
                    <span>{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
                </div>
                <div className="w-full h-2 md:h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-teal-400 to-teal-600 transition-all duration-500 ease-out rounded-full" style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }} />
                </div>
            </div>

            <div className={`flex-1 flex flex-col justify-center transition-opacity duration-300 ${isTransitioning ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>
                <span className="text-teal-600 dark:text-teal-400 font-bold uppercase text-[10px] md:text-xs tracking-widest mb-3 md:mb-4 block">
                    Questão {currentQuestion + 1} de {questions.length}
                </span>
                
                <h2 className="text-lg md:text-3xl font-bold text-slate-800 dark:text-slate-100 mb-6 md:mb-10 leading-snug">
                    {currentQ.text}
                </h2>

                <div className="grid grid-cols-1 gap-3 md:gap-4">
                    {currentQ.options.map((option, idx) => (
                        <button key={idx} onClick={() => handleAnswer(option.type)} className="w-full text-left p-4 md:p-6 rounded-xl md:rounded-2xl border-2 border-slate-100 dark:border-slate-800 hover:border-teal-500 dark:hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 active:bg-teal-100 transition-all duration-200 group flex items-center justify-between bg-white dark:bg-slate-900 shadow-sm hover:shadow-md touch-manipulation">
                            <span className="font-medium text-sm md:text-lg text-slate-700 dark:text-slate-300 group-hover:text-teal-700 dark:group-hover:text-teal-300">
                                {option.text}
                            </span>
                            <div className="w-5 h-5 md:w-6 md:h-6 rounded-full border-2 border-slate-300 dark:border-slate-600 group-hover:border-teal-500 group-hover:bg-teal-500 transition-all flex-shrink-0 ml-3 md:ml-4" />
                        </button>
                    ))}
                </div>
            </div>
          </div>
        )}

        {/* PASSO 3: RESULTADO */}
        {step === 3 && result && (
          <div ref={resultRef} className="p-6 md:p-10 bg-white dark:bg-slate-900 print:p-0 animate-in fade-in duration-700">
            <div className="hidden print:block mb-8 border-b pb-4">
              <h1 className="text-3xl font-bold text-slate-900">Relatório DISC - Visão Geral</h1>
              <p className="text-slate-500 mt-1">Candidato: <strong>{userForm.name}</strong> | Idade: {userForm.age} anos</p>
            </div>

            <div className="text-center mb-8 md:mb-12 print:hidden" data-html2canvas-ignore="true">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-sm animate-bounce-slow">
                <CheckCircle size={32} className="md:w-10 md:h-10" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">Análise Concluída!</h2>
              <p className="text-sm md:text-base text-slate-500 dark:text-slate-400 mt-2">Veja abaixo o mapeamento do seu perfil.</p>
            </div>

            <div className="mb-10 md:mb-16 print:mb-8">
               <div className="bg-slate-50 dark:bg-slate-800/50 p-4 md:p-8 rounded-2xl md:rounded-3xl border border-slate-100 dark:border-slate-800 print:bg-white print:border-none print:p-0">
                   <h3 className="text-lg md:text-xl font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                       <BarChart2 className="w-5 h-5 text-teal-500" /> Distribuição Comportamental
                   </h3>
                   <BarChart data={result} />
               </div>
            </div>

             <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12 print:mb-8">
                {['D', 'I', 'S', 'C'].map(trait => (
                    <div key={trait} className={`flex flex-col items-center p-3 md:p-5 rounded-2xl border ${PROFILES_INFO[trait].bg} ${PROFILES_INFO[trait].border} bg-opacity-20 border-opacity-30 dark:bg-opacity-10 dark:border-opacity-30`}>
                        <span className={`text-2xl md:text-4xl font-black ${PROFILES_INFO[trait].color} mb-1`}>{result[trait]}%</span>
                        <span className={`font-bold text-[10px] md:text-sm uppercase tracking-wider ${PROFILES_INFO[trait].color} opacity-80 text-center`}>{PROFILES_INFO[trait].title.split(' ')[1]}</span>
                    </div>
                ))}
            </div>

            <div className="mt-8 md:mt-12 pt-8 border-t border-slate-200 dark:border-slate-700 print:border-none print:mt-0 print:pt-0 print:break-before-page">
              <div className="bg-white dark:bg-slate-900 rounded-2xl print:p-0">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6">
                    <div className={`p-3 rounded-xl ${PROFILES_INFO[result.dominantTrait].bg} bg-opacity-50`}>
                        <User className={`w-8 h-8 ${PROFILES_INFO[result.dominantTrait].color}`} />
                    </div>
                    <div>
                        <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 uppercase tracking-widest font-bold">Perfil Predominante</p>
                        <h2 className={`text-2xl md:text-3xl font-bold ${PROFILES_INFO[result.dominantTrait].color}`}>
                            {PROFILES_INFO[result.dominantTrait].fullTitle}
                        </h2>
                    </div>
                </div>
                
                <p className="text-slate-700 dark:text-slate-300 mb-8 md:mb-10 leading-relaxed text-base md:text-lg border-l-4 border-slate-200 dark:border-slate-700 pl-4 md:pl-6 italic">
                  "{PROFILES_INFO[result.dominantTrait].description}"
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-10">
                  <div className="space-y-6">
                    <div>
                        <h3 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">✨ Características</h3>
                        <ul className="space-y-2">
                        {PROFILES_INFO[result.dominantTrait].characteristics.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-3 text-sm md:text-base text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PROFILES_INFO[result.dominantTrait].color.replace('text-', 'bg-')}`} />
                                {item}
                            </li>
                        ))}
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">💪 Pontos Fortes</h3>
                        <ul className="space-y-2">
                        {PROFILES_INFO[result.dominantTrait].strengths.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-3 text-sm md:text-base text-slate-600 dark:text-slate-400 bg-green-50 dark:bg-green-900/10 p-2 rounded-lg">
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                {item}
                            </li>
                        ))}
                        </ul>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                        <h3 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">⚠️ Pontos de Atenção</h3>
                        <ul className="space-y-2">
                        {PROFILES_INFO[result.dominantTrait].pointsToImprove.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-3 text-sm md:text-base text-slate-600 dark:text-slate-400 bg-red-50 dark:bg-red-900/10 p-2 rounded-lg">
                                <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                                {item}
                            </li>
                        ))}
                        </ul>
                    </div>
                    
                    {ageInsight && (
                        <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-5 md:p-6 rounded-2xl text-white shadow-lg print:border print:border-slate-300 print:bg-white print:text-black mt-4 md:mt-0">
                            <div className="flex items-center gap-3 mb-3">
                                <Clock className="w-5 h-5 text-teal-400" />
                                <h3 className="text-base md:text-lg font-bold">Momento de Carreira ({ageInsight.stage})</h3>
                            </div>
                            <p className="text-slate-300 print:text-slate-600 text-xs md:text-sm mb-4 leading-relaxed opacity-90">
                                {ageInsight.description}
                            </p>
                            <div className="bg-white/10 p-3 rounded-lg print:bg-slate-100">
                                <h4 className="font-bold text-teal-300 print:text-teal-700 flex items-center gap-2 mb-1 text-xs md:text-sm">
                                    <TrendingUp className="w-4 h-4" /> Conselho Estratégico
                                </h4>
                                <p className="text-[11px] md:text-xs text-white print:text-slate-800 italic">"{ageInsight.advice}"</p>
                            </div>
                        </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* --- ÁREA DE PROPAGANDA --- */}
            <div className="mt-8 mb-8 p-6 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-100 dark:border-emerald-800/50 print:hidden text-center shadow-sm" data-html2canvas-ignore="true">
                <h3 className="text-lg md:text-xl font-bold text-emerald-800 dark:text-emerald-300 mb-2">
                    Quer entender a fundo seu perfil?
                </h3>
                <p className="text-sm md:text-base text-emerald-700 dark:text-emerald-400 mb-4 max-w-2xl mx-auto">
                    A análise comportamental completa pode transformar sua carreira. Consulte uma especialista para um direcionamento personalizado.
                </p>
                
                <div className="mb-5">
                    <span className="text-sm font-medium text-emerald-600/80 dark:text-emerald-400/80 uppercase tracking-wide">Investimento</span>
                    <div className="text-3xl md:text-4xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                        R$ 49,99
                    </div>
                </div>

                <Button onClick={handleWhatsApp} className="bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full px-8 py-6 shadow-lg shadow-green-600/20 transition-all hover:scale-105">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Falar com Especialista no WhatsApp
                </Button>
            </div>

            {/* BOTÕES DE AÇÃO */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 print:hidden border-t dark:border-slate-800 pt-8 mt-8" data-html2canvas-ignore="true">
              <Button onClick={handleReset} variant="ghost" className="w-full sm:flex-1 py-4 md:py-6 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm md:text-base">
                <RefreshCw className="mr-2 w-4 h-4 md:w-5 md:h-5" /> Refazer
              </Button>
              <Button onClick={generatePDF} variant="outline" className="w-full sm:flex-1 py-4 md:py-6 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm md:text-base">
                <Download className="mr-2 w-4 h-4 md:w-5 md:h-5" /> Baixar PDF
              </Button>
              <Button onClick={handleEmailDirect} disabled={isSending} className="w-full sm:flex-[2] py-4 md:py-6 bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-600/20 text-sm md:text-base disabled:opacity-50">
                <Send className="mr-2 w-4 h-4 md:w-5 md:h-5" /> 
                {isSending ? "Enviando..." : "Enviar Resultado"}
              </Button>
            </div>
            
            <p className="text-xs text-center text-slate-400 dark:text-slate-500 mt-6 print:hidden opacity-60" data-html2canvas-ignore="true">
              Ao clicar em "Baixar", salve como PDF nas opções de impressão.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscTestPage;